import React from 'react';
import { motion } from 'framer-motion';

interface DeepProgressRingProps {
  progress: number; // 0-100
  size?: number;
  strokeWidth?: number;
  /** When true, ring is full and uses success/overtime styling */
  isOvertime?: boolean;
  children?: React.ReactNode;
}

export const DeepProgressRing = React.memo(function DeepProgressRing({ 
  progress, 
  size = 300, 
  strokeWidth = 2,
  isOvertime = false,
  children 
}: DeepProgressRingProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        className="absolute inset-0 transform -rotate-90"
        width={size}
        height={size}
      >
        {/* Gradient definition - success green when overtime */}
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="50%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#06b6d4" />
          </linearGradient>
          <linearGradient id="progressGradientOvertime" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22c55e" />
            <stop offset="100%" stopColor="#16a34a" />
          </linearGradient>
          
          {/* Glow filter for pulsing effect */}
          <filter id="progressGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background ring - theme-aware, softened to avoid hardline */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={1}
          className="text-foreground/5"
        />

        {/* Progress ring with glow */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={isOvertime ? 'url(#progressGradientOvertime)' : 'url(#progressGradient)'}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{
            strokeDashoffset,
            filter: ['url(#progressGlow)', 'none', 'url(#progressGlow)'],
          }}
          transition={{
            strokeDashoffset: { duration: 1, ease: 'linear' },
            filter: { duration: 6, repeat: Infinity, ease: 'easeInOut' },
          }}
        />
      </svg>

      {/* Content inside the ring */}
      <div className="absolute inset-0 flex items-center justify-center">
        {children}
      </div>
    </div>
  );
});
